import * as React from 'react'
import { Loader2, Lock, Search, X } from 'lucide-react'
import {
  isMainBranch,
  useActiveBranch,
  useListBranchesQuery,
  useSwitchBranch,
  type Branch
} from '@axilon-apps/domains-ontology-ui-state'
import { BranchActionsMenu } from '../../../components/Branches/BranchActionsMenu'
import { formatRelativeTime } from '../../../lib/time'
import { cn } from '../../../lib/utils'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { useDebouncedValue } from '../../../lib/use-debounced-value'
import { BranchRow } from './BranchRow'

const PAGE_SIZE = 20
// Protected branches (main + any frozen branch) are few; one generous page
// covers them in their pinned section without its own pager.
const PROTECTED_PAGE_SIZE = 50

const PAGER_SLOTS = 7

/** Page-number slots for the pager. Returns a *constant* number of slots
 * (`PAGER_SLOTS`) once paginated: the first and last page always, a 3-wide
 * window around the current page, and the two remaining slots filled by either
 * '…' or the page adjacent to a boundary. The fixed count keeps the pager's
 * width from shifting as you move between pages. */
export function pageNumbers(current: number, total: number): (number | '…')[] {
  if (total <= PAGER_SLOTS) return Array.from({ length: total }, (_, i) => i + 1)

  // The middle window stays 3 wide; clamped so it never overruns the boundaries.
  const start = Math.max(Math.min(current - 1, total - 4), 3)
  const end = Math.min(Math.max(current + 1, 5), total - 2)

  const out: (number | '…')[] = [1]
  // Slot between page 1 and the window: an ellipsis, unless only page 2 hides
  // there — then show page 2 itself rather than collapse a single page.
  out.push(start > 3 ? '…' : 2)
  for (let p = start; p <= end; p++) out.push(p)
  out.push(end < total - 2 ? '…' : total - 1)
  out.push(total)
  return out
}

function branchDetail(branch: Branch): string {
  if (isMainBranch(branch)) return 'baseline · protected'
  const parts: string[] = []
  if (branch.protected) parts.push('protected')
  if (branch.forkedFrom) {
    // forkedFrom is the commit hash the branch was created off.
    parts.push(`forked from ${branch.forkedFrom.slice(0, 8)}`)
  }
  const lifecycle = lifecycleSummary(branch)
  parts.push(lifecycle.when)
  if (lifecycle.actor) parts.push(`by ${lifecycle.actor}`)
  return parts.filter(Boolean).join(' · ')
}

/** Picks the timestamp + actor that best summarizes the branch's current state. */
function lifecycleSummary(branch: Branch): { when: string; actor: string } {
  if (branch.status === 'merged' && branch.mergedAt) {
    return { when: `merged ${formatRelativeTime(branch.mergedAt)}`, actor: branch.mergedBy ?? '' }
  }
  if (branch.status === 'deleted' && branch.deletedAt) {
    return {
      when: `deleted ${formatRelativeTime(branch.deletedAt)}`,
      actor: branch.deletedBy ?? ''
    }
  }
  return { when: formatRelativeTime(branch.createdAt), actor: branch.createdBy ?? '' }
}

function extractApiError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: unknown }).data
    if (data && typeof data === 'object' && 'message' in data) {
      const m = (data as { message?: unknown }).message
      if (typeof m === 'string' && m.length > 0) return m
    }
  }
  return fallback
}

export function BranchesCard() {
  const [showHistory, setShowHistory] = React.useState(false)
  const [page, setPage] = React.useState(1)
  const [origin, setOrigin] = React.useState<'all' | 'user' | 'system'>('all')
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebouncedValue(search, 250)
  const query = debouncedSearch.trim()
  const originFilter = origin === 'all' ? undefined : origin

  // Two queries: the protected branches (main + any frozen branch) shown in
  // their own pinned section above the list, and the unprotected branches in
  // the numbered, paginated list below. Splitting them keeps the protected ones
  // always at the top, even though they are the oldest rows (so at scale they
  // would otherwise sit on the last page). Both respect the population filter.
  const protectedQuery = useListBranchesQuery({
    includeDeleted: showHistory,
    origin: originFilter,
    protected: true,
    ...(query ? { q: query } : {}),
    pageSize: PROTECTED_PAGE_SIZE
  })
  const listQuery = useListBranchesQuery({
    includeDeleted: showHistory,
    origin: originFilter,
    protected: false,
    ...(query ? { q: query } : {}),
    offset: (page - 1) * PAGE_SIZE,
    pageSize: PAGE_SIZE
  })
  const protectedBranches = protectedQuery.data?.branches ?? []
  const protectedTotal = protectedQuery.data?.totalSize ?? 0
  const branches = listQuery.data?.branches ?? []
  const total = listQuery.data?.totalSize ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const isLoading = listQuery.isLoading || protectedQuery.isLoading
  const { isFetching } = listQuery
  const error = listQuery.error ?? protectedQuery.error
  const { switchBranch, isOpening } = useSwitchBranch()
  const activeId = useActiveBranch()
  const [switchError, setSwitchError] = React.useState('')

  // Flipping the merged-&-deleted view changes the result set; restart at page 1.
  const handleShowHistory = React.useCallback(() => {
    setShowHistory((prev) => !prev)
    setPage(1)
  }, [])

  // Clamp the page if the result set shrank under it (e.g. after a delete).
  React.useEffect(() => {
    if (listQuery.data && page > totalPages) setPage(totalPages)
  }, [listQuery.data, page, totalPages])

  const handleSelect = async (branch: Branch) => {
    const targetId = isMainBranch(branch) ? undefined : branch.id
    if ((targetId ?? undefined) === (activeId ?? undefined)) return
    setSwitchError('')
    try {
      await switchBranch(targetId)
    } catch (err) {
      setSwitchError(extractApiError(err, `Could not open ${branch.name}.`))
    }
  }

  const renderBranchRow = (branch: Branch) =>
    branch.status === 'active' ? (
      <BranchRow
        key={branch.id}
        name={branch.name}
        detail={branchDetail(branch)}
        isActive={isMainBranch(branch) ? !activeId : activeId === branch.id}
        onSelect={() => void handleSelect(branch)}
        actions={<BranchActionsMenu branch={branch} />}
        origin={branch.origin}
      />
    ) : (
      <BranchRow
        key={branch.id}
        name={branch.name}
        detail={branchDetail(branch)}
        isActive={false}
        lifecycle={branch.status}
        origin={branch.origin}
      />
    )

  const grandTotal = protectedTotal + total

  return (
    <Card className="gap-3 py-4">
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-ink">Branches</h3>
          <span className="text-xs text-ink-3">
            {grandTotal} {grandTotal === 1 ? 'branch' : 'branches'}
          </span>
        </div>

        <div className="flex flex-col gap-3">
          <div className="relative">
            <Label htmlFor="settings-branch-search" className="sr-only">
              Search branches
            </Label>
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-ink-3" />
            <Input
              id="settings-branch-search"
              type="search"
              placeholder="Search branch names"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
              className="h-8 pl-8 pr-8 text-xs"
            />
            {search ? (
              <button
                type="button"
                aria-label="Clear branch search"
                onClick={() => {
                  setSearch('')
                  setPage(1)
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-ink-3 transition-colors hover:text-ink"
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </div>

          {/* Population filter: the user-facing branches (user + agent) vs the
              machine-managed ones (system + ingest), mirroring the picker split. */}
          <div className="flex items-center gap-1">
            {(['all', 'user', 'system'] as const).map((o) => (
              <Button
                key={o}
                type="button"
                variant={origin === o ? 'outline' : 'ghost'}
                size="xs"
                className={cn('capitalize', origin === o && 'text-brand')}
                onClick={() => {
                  setOrigin(o)
                  setPage(1)
                }}
              >
                {o}
              </Button>
            ))}
          </div>

          {error ? (
            <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
              Failed to load branches.
            </div>
          ) : null}
          {switchError ? (
            <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
              {switchError}
            </div>
          ) : null}

          {protectedBranches.length > 0 ? (
            <div className="flex flex-col">
              <div className="flex items-center gap-1.5 pb-1 text-xs font-medium text-ink-3">
                <Lock className="size-3" />
                Protected
              </div>
              {protectedBranches.map(renderBranchRow)}
            </div>
          ) : null}

          <div className="flex flex-col">
            {protectedBranches.length > 0 ? (
              <div className="pb-1 text-xs font-medium text-ink-3">Branches</div>
            ) : null}
            {branches.map(renderBranchRow)}
            {query &&
            !isLoading &&
            branches.length === 0 &&
            protectedBranches.length === 0 &&
            !error ? (
              <p className="px-1 py-2 text-xs text-ink-3">No branches match '{query}'.</p>
            ) : null}
            {!query &&
            !isLoading &&
            branches.length === 0 &&
            protectedBranches.length === 0 &&
            !error ? (
              <p className="py-2.5 text-xs text-ink-3">No branches on this page.</p>
            ) : null}
            {isLoading ? (
              <div className="flex items-center gap-2 py-2.5 text-xs text-ink-3">
                <Loader2 className="size-3 animate-spin" />
                Loading branches…
              </div>
            ) : null}
            {isOpening ? (
              <div className="flex items-center gap-2 py-2.5 text-xs text-ink-3">
                <Loader2 className="size-3 animate-spin" />
                Opening branch — building its working database…
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={handleShowHistory}
              className="cursor-pointer text-xs text-ink-3 underline underline-offset-2 hover:text-ink"
            >
              {showHistory ? 'Hide merged & deleted' : 'Show merged & deleted'}
            </button>
            {total > 0 ? (
              <span className="text-xs text-ink-3">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </span>
            ) : null}
          </div>

          {totalPages > 1 ? (
            <div className="flex items-center justify-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="xs"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </Button>
              {pageNumbers(page, totalPages).map((p, i) =>
                p === '…' ? (
                  <span
                    key={`gap-${i}`}
                    aria-hidden
                    className="inline-flex h-6 min-w-7 items-center justify-center font-mono text-xs text-ink-3"
                  >
                    …
                  </span>
                ) : (
                  <Button
                    key={p}
                    type="button"
                    variant={p === page ? 'outline' : 'ghost'}
                    size="xs"
                    disabled={isFetching}
                    className={cn('min-w-7 font-mono', p === page && 'text-brand')}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </Button>
                )
              )}
              <Button
                type="button"
                variant="ghost"
                size="xs"
                disabled={page >= totalPages || isFetching}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
            </div>
          ) : null}

          <p className="text-xs text-ink-3">
            Click a branch to make it active. Opening a branch for the first time builds its working
            copy (a few seconds); graph and ingest pages refetch with the new context.
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
